import { useEffect, useState } from 'react';
import { isAxiosError } from 'axios';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@werkstatt/ui';
import { Button } from '@werkstatt/ui';
import { Input } from '@werkstatt/ui';
import { Label } from '@werkstatt/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@werkstatt/ui';
import { useToast } from '../../hooks/use-toast';
import api from '../../lib/api';
import type { Contract, ContractCreate } from '../../types/contract';
import type { Vehicle } from '../../types/vehicle';
import type { Customer } from '../../types/customer';
import type { Booking } from '../../types/booking';
import { formatPrice } from '../../types/contract';

interface ContractDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contract: Contract | null;
  onSuccess: () => void;
}

export function ContractDialog({
  open,
  onOpenChange,
  contract,
  onSuccess,
}: ContractDialogProps) {
  const { toast } = useToast();
  const isEdit = !!contract;
  const isView = isEdit; // For now, treat edit mode as view mode

  const [formData, setFormData] = useState<Partial<ContractCreate>>({
    contract_number: '',
    customer_id: 0,
    vehicle_id: 0,
    booking_id: undefined,
    contract_data: {},
    status: 'active',
  });

  // Fetch customers
  const { data: customersData } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      const response = await api.get('/customers?page_size=100');
      return response.data;
    },
  });

  // Fetch vehicles
  const { data: vehiclesData } = useQuery({
    queryKey: ['vehicles'],
    queryFn: async () => {
      const response = await api.get('/vehicles?page_size=100');
      return response.data;
    },
  });

  // Fetch bookings
  const { data: bookingsData } = useQuery({
    queryKey: ['bookings'],
    queryFn: async () => {
      const response = await api.get('/bookings?page_size=100');
      return response.data;
    },
  });

  // Reset form when dialog opens/closes or contract changes
  useEffect(() => {
    if (contract) {
      setFormData({
        contract_number: contract.contract_number,
        customer_id: contract.customer_id,
        vehicle_id: contract.vehicle_id,
        booking_id: contract.booking_id || undefined,
        contract_data: contract.contract_data,
        status: contract.status,
      });
    } else {
      // Generate contract number
      const now = new Date();
      const year = now.getFullYear();
      const contractNum = `MV-${year}-${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}`;

      setFormData({
        contract_number: contractNum,
        customer_id: 0,
        vehicle_id: 0,
        booking_id: undefined,
        contract_data: {},
        status: 'active',
      });
    }
  }, [contract, open]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: ContractCreate) => {
      const response = await api.post('/contracts', data);
      return response.data;
    },
    onSuccess: () => {
      toast({
        title: 'Vertrag erstellt',
        description: 'Der Vertrag wurde erfolgreich erstellt.',
      });
      onSuccess();
    },
    onError: (error) => {
      toast({
        title: 'Fehler',
        description:
          (isAxiosError(error) ? error.response?.data?.detail : undefined) ||
          'Der Vertrag konnte nicht erstellt werden.',
        variant: 'destructive',
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate
    if (!formData.customer_id || formData.customer_id === 0) {
      toast({
        title: 'Fehler',
        description: 'Bitte wählen Sie einen Kunden aus.',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.vehicle_id || formData.vehicle_id === 0) {
      toast({
        title: 'Fehler',
        description: 'Bitte wählen Sie ein Fahrzeug aus.',
        variant: 'destructive',
      });
      return;
    }

    createMutation.mutate(formData as ContractCreate);
  };

  const isLoading = createMutation.isPending;

  const customers: Customer[] = customersData?.customers || [];
  const vehicles: Vehicle[] = vehiclesData?.vehicles || [];
  const bookings: Booking[] = bookingsData?.bookings || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">
            {isView ? 'Vertrag anzeigen' : 'Neuer Vertrag'}
          </DialogTitle>
        </DialogHeader>

        {isView && contract ? (
          // View mode - show contract details
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Vertragsnummer</Label>
                <div className="font-medium">{contract.contract_number}</div>
              </div>
              <div>
                <Label>Status</Label>
                <div className="font-medium">{contract.status}</div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Kunde</Label>
                <div>{contract.customer_name || `Kunde #${contract.customer_id}`}</div>
              </div>
              <div>
                <Label>Fahrzeug</Label>
                <div>{contract.vehicle_name || `Fahrzeug #${contract.vehicle_id}`}</div>
              </div>
            </div>

            {contract.pickup_protocol && (
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-2">Übergabeprotokoll</h3>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <Label className="text-xs">KM-Stand</Label>
                    <div>{contract.mileage_start?.toLocaleString('de-DE')} km</div>
                  </div>
                  <div>
                    <Label className="text-xs">Tankfüllung</Label>
                    <div>{contract.fuel_level_start}/8</div>
                  </div>
                  <div>
                    <Label className="text-xs">Schäden</Label>
                    <div>{contract.damages_at_pickup.length}</div>
                  </div>
                </div>
              </div>
            )}

            {contract.return_protocol && (
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-2">Rückgabeprotokoll</h3>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <Label className="text-xs">KM-Stand</Label>
                    <div>{contract.mileage_end?.toLocaleString('de-DE')} km</div>
                  </div>
                  <div>
                    <Label className="text-xs">Gefahrene KM</Label>
                    <div>{contract.mileage_driven?.toLocaleString('de-DE')} km</div>
                  </div>
                  <div>
                    <Label className="text-xs">Tankfüllung</Label>
                    <div>{contract.fuel_level_end}/8</div>
                  </div>
                </div>

                {contract.final_total_cents && (
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold">Endsumme:</span>
                      <span className="text-lg font-bold">
                        {formatPrice(contract.final_total_cents)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end pt-4 border-t">
              <Button onClick={() => onOpenChange(false)}>Schließen</Button>
            </div>
          </div>
        ) : (
          // Create mode - show form
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <h3 className="font-semibold">Vertragsdetails</h3>
              <div>
                <Label htmlFor="contract_number">Vertragsnummer *</Label>
                <Input
                  id="contract_number"
                  required
                  value={formData.contract_number}
                  onChange={(e) =>
                    setFormData({ ...formData, contract_number: e.target.value })
                  }
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="customer_id">Kunde *</Label>
                  <Select
                    value={formData.customer_id?.toString()}
                    onValueChange={(value: string) =>
                      setFormData({ ...formData, customer_id: parseInt(value) })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Kunde auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map((customer) => (
                        <SelectItem key={customer.id} value={customer.id.toString()}>
                          {customer.first_name} {customer.last_name}
                          {customer.customer_number && ` (${customer.customer_number})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="vehicle_id">Fahrzeug *</Label>
                  <Select
                    value={formData.vehicle_id?.toString()}
                    onValueChange={(value: string) =>
                      setFormData({ ...formData, vehicle_id: parseInt(value) })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Fahrzeug auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {vehicles.map((vehicle) => (
                        <SelectItem key={vehicle.id} value={vehicle.id.toString()}>
                          {vehicle.manufacturer} {vehicle.model} ({vehicle.internal_number})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="booking_id">Zugehörige Buchung (Optional)</Label>
                <Select
                  value={formData.booking_id?.toString() || undefined}
                  onValueChange={(value: string) =>
                    setFormData({
                      ...formData,
                      booking_id: value ? parseInt(value) : undefined,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Buchung auswählen (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {bookings.map((booking) => (
                      <SelectItem key={booking.id} value={booking.id.toString()}>
                        {booking.booking_number}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                Abbrechen
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Wird erstellt...' : 'Erstellen'}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
